/* eslint-disable import/first */
import './public-path'; // Don't try to re-order this, it needs to be imported before any other modules
import React from 'react';
import ReactDOM from 'react-dom';
import { isEmpty } from 'underscore';
import { getCldrLocale, setupI18nModel } from '@bingads-webui/i18n-model-setup';
import { initGlobalService, PROJECTS } from '@bingads-webui-campaign/global-app-init';
import { convertLegacyContext } from '@bingads-webui-campaign-react/server-context';
import { getInstrumentation, surveySchema } from '@bingads-webui-campaign-react/pmax-lite-instrumentation';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { PageReady } from '@bingads-webui/page-ready';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import './index.less';
import { isAcceptInvitationRoute } from './route-utils';

window.performance.preAppStartMarker = {
  eventName: 'ActivityMarker/AppStart.BeforeLoad',
  eventStartTime: window.performance.now(),
  activeTimeBeforeEventStart: window.performance.activeTime(),
};

// window.serverContext and window.paymentContext
// are specified by the server side through responded HTML.
// To avoid any code depends on window.serverContext directly,
// remove it after passing it to the <App />
// [Updated]- Revert deletion for window.serverContext since legacy components need it
const { paymentContext } = window;
const devConfig = window.devConfig || {
  isInDevelopmentMode: false,
};
const serverContext = convertLegacyContext(window.serverContext);
const subSystemName = 'pmax-lite-signup';
const instrumentation = getInstrumentation({ serverContext, devConfig, subSystemName });
instrumentation.addScenario(surveySchema);

const appScenario = instrumentation.appScenario.create();
const appActivity = appScenario.appActivity.create();

delete window.paymentContext;

if (serverContext.CurrentAccount) {
  window.isDelayedEdmSetup = false;
  // This is needed for edm campaign odata calls
  window.campaignODataConfigs = {
    AdvertiserCustomerID: serverContext.CurrentAccount.AdvertiserCustomerID,
    AccountID: serverContext.CurrentAccount.Id,
    Token: serverContext.Token,
    ServiceRoot: `${serverContext.ServicesEndpoints.CampaignAPI}${serverContext.CampaignAPIPath}`,
  };
} else {
  window.isDelayedEdmSetup = true;
}

// hard code it because under multi-user situation, user could have a non-English setting
if (!serverContext.CurrentLocale) {
  serverContext.CurrentLocale = 'en-US';
}

serverContext.UserPreferences = serverContext.UserPreferences || {
  AccountLevelPreferences: null,
  UserLevelPreferences: null,
};

serverContext.IsAADUser = serverContext.AuthType === 'AAD';
serverContext.McaServiceRoot = `${serverContext.ServicesEndpoints.CampaignAPI}${serverContext.McaAPIPath}`;

serverContext.Permissions = serverContext.Permissions || {};

// Ad extension related
serverContext.Permissions.IsActionLinkExtensionAllowed = true;
serverContext.Permissions.IsPriceExtensionAllowed = true;
serverContext.Permissions.IsLocationExtensionAllowed = false;
serverContext.Permissions.IsAppExtensionAllowed = true;
serverContext.Permissions.IsSiteLinkExtensionsAllowed = true;
serverContext.Permissions.IsCallExtensionAllowed = false;
serverContext.Permissions.IsCalloutExtensionAllowed = true;
serverContext.Permissions.IsReviewExtensionAllowed = true;
serverContext.Permissions.IsStructuredSnippetExtensionAllowed = true;
serverContext.Permissions.IsPromotionAdExtensionEnabled = true;
serverContext.Permissions.IsFilterLinkAdExtensionEnabled = true;
serverContext.Permissions.IsFlyerAdExtensionEnabled = false;

serverContext.SupportOptions.IfShowRequestCall =
  !isEmpty(serverContext.SupportOptions.RequestCallText);

// workaround: if in CI env, need to update AdInsightAPI path in serverContext
const hostname = document.location.hostname.toLowerCase();
if (hostname.indexOf('ccui.devdev') > -1) {
  const {
    AdInsightAPI: adInsightAPI,
    CampaignAPI: campaignAPI,
  } = serverContext.ServicesEndpoints || {};

  if (campaignAPI && adInsightAPI && adInsightAPI.indexOf('apps-devdev') > -1) {
    const ciHostname = new URL(campaignAPI).hostname;
    const subdomain = ciHostname.split('.')[0];
    serverContext.ServicesEndpoints.AdInsightAPI = adInsightAPI.replace('apps-devdev', subdomain);
  }
}

const locale = serverContext.CurrentLocale || document.documentElement.lang;
const cldrLocale = getCldrLocale(locale);

delete window.devConfig;

// do not start keep-alive polling if Token has not been created yet
const shouldStartKeepAlive = serverContext.Token != null;
const shouldSetQueryParams = serverContext.Token != null;

const initialize = () => {
  if (isSDK()) {
    const sdkPITypeAllowList = [
      1, // PaymentInstrumentType.CreditCard,
      7, // PaymentInstrumentType.OfflinePaymentMethod,
    ];
    paymentContext.SupportedPiTypes = paymentContext.SupportedPiTypes
      .filter(id => sdkPITypeAllowList.indexOf(id) !== -1);

    serverContext.ServicesEndpoints.ClientCenterUIAPI = '/cc/';
  }

  // Surveys and CFAR ads belong to the signup wizard, not to accepting an invitation.
  const isInvitationRoute = isAcceptInvitationRoute();

  initGlobalService({
    featureName: PROJECTS.PMAX_LITE_SIGNUP,
    serverContext,
    shouldLoadAdobe: true,
    shouldSetupQualtrics: !isInvitationRoute,
    shouldStartKeepAlive,
    shouldSetQueryParams,
    shouldLoadAmplifyTracking: true,
    shouldLoadCFAR: !isInvitationRoute,
    setUpClarityBasedOnUserConsent: true,
    appActivity,
  });

  if (window.location.port === '12000') {
    import('jquery').then(({ default: $ }) => {
      const originalAjax = $.ajax;
      // eslint-disable-next-line no-param-reassign
      $.ajax = (...args) => {
        const [options, ...rest] = args;
        // In signup proxy, we need "crossDomain=false" for cc API calls otherwise they return 302
        if (options && options.url && options.url.indexOf('cc/') !== -1) {
          return originalAjax({
            ...options,
            crossDomain: false,
          }, ...rest);
        }
        return originalAjax(...args);
      };
    });
  }

  if (window.performance.preAppStartMarker) {
    appActivity.perf(window.performance.preAppStartMarker);
    delete window.performance.preAppStartMarker;
  }

  import(/* webpackChunkName: "cldr-[request]", webpackPrefetch: true */`@bingads-webui-universal/i18n-cldr-data-modern/locales/${cldrLocale}.json`)
    .then(setupI18nModel(locale))
    .then(async () => {
      if (serverContext.CurrentAccount) {
        // need legacy initialization here for resume scenario
        // for non-resume scenario, legacy components will be initialized in the wizard step when needed
        try {
          const { initLegacyComponentsWithServerContext } = await import(/* webpackChunkName: "legacy-init" */'@bingads-webui-campaign-react/pmax-lite-wizard-utils');
          initLegacyComponentsWithServerContext(serverContext);
          window.isLegacyInitDone = true;
        } catch (error) {
        // Log error but don't block app loading
        // eslint-disable-next-line no-console
          window.isLegacyInitDone = false;
          console.error('Failed to initialize legacy components:', error);
        }
      } else {
        window.isLegacyInitDone = false;
      }

      // The invitation route bypasses PmaxLiteSignupAppCore, so no wizard step ever signals
      // page-ready — which leaves initGlobalService's Clarity/WCP-consent/Adobe/Amplify block
      // (gated on PageReady.afterPageReady) permanently pending. The wizard path keeps owning its
      // own timing.
      if (isAcceptInvitationRoute()) {
        PageReady.pageIsReady();
      }

      // eslint-disable-next-line global-require
      const AppRoot = require('./app').default;

      ReactDOM.render(
        <AppRoot
          serverContext={serverContext}
          paymentContext={paymentContext}
          devConfig={devConfig}
        />,
        document.getElementById('root')
      );
    });
};

if (isSDK()) {
  import(/* webpackChunkName: "sdk-bootstrap" */'@bingads-webui-campaign-react/sdk-bootstrap').then(({ sdkBootstrap }) => {
    sdkBootstrap(serverContext, initialize);
  });
} else {
  initialize();
}
